'use client';

import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

import { SloganSvg } from './SloganSvg';

const CONTAINER_ANIMATION = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const ITEM_ANIMATION = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
};

export const ProgramIntroSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <section className="relative bg-Gray-black px-32 py-80 flex-center">
      <motion.svg
        width="83"
        height="82"
        viewBox="0 0 83 82"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute -top-41 right-30"
        animate={{
          y: [0, -10, 0],
        }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <path
          d="M1.59 52.21C7.85 73.96 30.79 86.62 52.85 80.428C74.86 74.246 87.67 51.543 81.41 29.79C75.15 8.04 52.17 -4.61 30.15 1.57C8.14 7.75 -4.67 30.45 1.59 52.21"
          fill="#1C9CFF"
        />
      </motion.svg>

      <motion.div
        ref={ref}
        variants={CONTAINER_ANIMATION}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        className="flex w-full flex-col gap-32"
      >
        <div className="flex flex-col gap-20">
          <motion.svg
            variants={ITEM_ANIMATION}
            width="120"
            height="31"
            viewBox="0 0 120 31"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_17759_497)">
              <path
                d="M101.469 28.7L102.045 28.2C103.021 27.35 103.668 26.8 103.668 25.837C103.668 24.76 102.861 23.82 101.459 23.82C100.057 23.82 99.1 24.7 99.05 26.1H100.564C100.626 25.54 100.875 25.26 101.389 25.26C101.956 25.26 102.143 25.61 102.143 25.91C102.143 26.279 101.956 26.47 100.794 27.485L98.98 29.07V30.15H103.784V28.71H101.451L101.469 28.7V28.7Z"
                fill="#535861"
              />
              <path
                d="M106.929 23.82C105.209 23.82 104.18 25.11 104.18 27.043C104.18 28.98 105.209 30.26 106.929 30.26C108.649 30.26 109.679 28.98 109.679 27.043C109.679 25.11 108.651 23.82 106.929 23.82ZM106.929 28.83C106.051 28.83 105.705 28.19 105.705 27.043C105.705 25.9 106.051 25.26 106.929 25.26C107.808 25.26 108.154 25.9 108.154 27.043C108.154 28.19 107.808 28.83 106.929 28.83Z"
                fill="#535861"
              />
              <path
                d="M112.481 28.7L113.057 28.2C114.033 27.35 114.68 26.8 114.68 25.837C114.68 24.76 113.873 23.82 112.471 23.82C111.069 23.82 110.111 24.7 110.059 26.1H111.576C111.638 25.54 111.886 25.26 112.4 25.26C112.968 25.26 113.154 25.61 113.154 25.91C113.154 26.279 112.968 26.47 111.806 27.485L109.988 29.07V30.15H114.795V28.71H112.462L112.481 28.7V28.7Z"
                fill="#535861"
              />
              <path
                d="M118.033 25.94C117.66 25.94 117.376 26.03 117.155 26.16L117.314 25.38H119.797V23.94H116.179L115.408 27.57H116.844C116.95 27.4 117.234 27.187 117.677 27.187C118.201 27.187 118.564 27.49 118.564 27.99C118.564 28.47 118.226 28.84 117.65 28.84C117.234 28.84 116.817 28.64 116.763 28.1H115.238C115.292 29.61 116.48 30.26 117.669 30.26C119.043 30.26 120.091 29.42 120.091 28.01C120.091 26.6 119.115 25.95 118.033 25.95V25.94Z"
                fill="#535861"
              />
              <path
                d="M102.218 18.22H85.91V21.19H102.218V18.22Z"
                fill="#535861"
              />
              <path
                d="M94.064 10.78L98.16 17.13H102.218L96.25 8.27L96.23 8.24H91.9L85.91 17.13H89.97L94.064 10.78Z"
                fill="#535861"
              />
              <path
                d="M118.63 12.54V7.87H115.105V21.47H118.63V15.79H120.089V12.54H118.63Z"
                fill="#535861"
              />
              <path
                d="M78.06 12.64H76.39V15.69H78.06V21.33H81.03V8.01H78.06V12.64Z"
                fill="#535861"
              />
              <path
                d="M76.0 11.28H76.85V8.44H68.61V11.28H69.46V18.33H68.61V20.9H76.85V18.33H76.0V11.28ZM73.05 18.33H72.42V11.28H73.05V18.33Z"
                fill="#535861"
              />
              <path
                d="M84.72 8.01H81.61V21.33H84.72V8.01Z"
                fill="#535861"
              />
              <path
                d="M114.226 18.22H107.071V15.72H114.226V12.89H107.071V11.12H114.226V8.15H103.574V21.19H114.226V18.22Z"
                fill="#535861"
              />
              <path
                d="M61.45 9.91H63.41V12.34H66.787V5.22H63.41V7.43H61.45V5.5H51.46V7.46H58.1V8.03H51.46V9.88H58.1V10.46H51.11V12.34H58.53C60.35 12.34 61.39 11.48 61.45 9.91H61.45Z"
                fill="#535861"
              />
              <path
                d="M46.46 8.74H34.76V14.32H49.98V11.82H38.22V11.24H49.92V5.68H34.7V8.18H46.46V8.74Z"
                fill="#535861"
              />
              <path
                d="M55.184 16.546H66.78V12.77H51.74V14.48H63.41V14.88H51.81V18.73H66.85V16.93H55.184V16.546Z"
                fill="#535861"
              />
              <path
                d="M44.132 14.74H40.68V16.23H34.42V18.86H50.39V16.23H44.132V14.74Z"
                fill="#535861"
              />
              <path
                d="M29.23 11.41H17.53V16.98H32.75V14.48H20.98V13.91H32.68V8.35H17.46V10.85H29.23V11.41Z"
                fill="#535861"
              />
              <path
                d="M26.9 17.41H23.45V18.89H17.19V21.53H33.16V18.89H26.9V17.41Z"
                fill="#535861"
              />
              <path
                d="M15.91 8.16H12.95V14.77H15.91V8.16Z"
                fill="#535861"
              />
              <path
                d="M9.66 14.77H12.56V8.16H9.66V9.64H8.57V12.002H9.66V14.77Z"
                fill="#535861"
              />
              <path
                d="M4.41 19.287H15.9V15.32H5.87V14.4H8.88V12.67H6.13C6.53 12.582 6.88 12.46 7.18 12.32C8.03 11.9 8.45 11.239 8.45 10.37C8.45 9.49 8.03 8.85 7.18 8.43C6.48 8.08 5.5 7.91 4.28 7.91C3.06 7.91 2.08 8.08 1.38 8.43C0.53 8.85 0.1 9.5 0.1 10.37C0.1 11.23 0.53 11.9 1.37 12.32C1.67 12.47 2.02 12.58 2.42 12.67H0V14.4H2.98V15.32H0.95V17.14H12.52V17.47H1.021V21.53H15.97V19.62H4.41V19.29V19.287ZM4.28 10.78C2.98 10.78 2.98 10.58 2.98 10.36C2.98 10.15 2.98 9.97 4.28 9.97C5.59 9.97 5.57 10.16 5.57 10.36C5.57 10.57 5.57 10.78 4.28 10.78Z"
                fill="#535861"
              />
            </g>
            <defs>
              <clipPath id="clip0_17759_497">
                <rect width="120" height="30.2609" fill="white" />
              </clipPath>
            </defs>
          </motion.svg>

          <div className="flex w-full justify-center">
            <SloganSvg
              className="h-auto w-full"
              style={{ aspectRatio: '262 / 70.88' }}
            />
          </div>
        </div>

        <div className="flex flex-col gap-16 font-400 text-Gray-550 typo-body3">
          <motion.p variants={ITEM_ANIMATION}>
            지역 균형발전을 돕는
            <br />
            거브테크(Gov-Tech) 기업 웰로가
            <br />
            열 곳의 지역과 함께
            <br />
            &apos;웰로 로컬페스타&apos;를 처음 개최합니다.
          </motion.p>

          <motion.p variants={ITEM_ANIMATION}>
            정책은 손으로 직접 느끼고,
            <br />
            마음으로 공감할 때 비로소 반응합니다.
          </motion.p>

          <motion.p variants={ITEM_ANIMATION}>
            고향의 가치와 로컬의 가능성을
            <br />
            오프라인에서 직접 경험하고 연결되는 시간
            <br />
            &apos;웰로 로컬페스타 2025&apos;에서 만나보세요.
          </motion.p>

          <motion.p variants={ITEM_ANIMATION}>
            지속가능한 지역의 미래,
            <br />
            웰로가 함께 만들어갑니다.
          </motion.p>
        </div>
      </motion.div>
    </section>
  );
};
